import { DefaultValue, selector, selectorFamily } from "recoil"

import { pluginStudioAtom } from "../atoms/pluginStudioAtom"
import {
  getCodeEditorPluginId,
  Plugin,
  PluginFile,
  PluginFileState,
  UserRole,
} from "../types"

/**
 * Selector for getting a plugin by ID
 */
export const pluginByIdSelector = selectorFamily<Plugin | null, string>({
  key: "pluginStudio.pluginById",
  get:
    (pluginId: string) =>
    ({ get }) => {
      const state = get(pluginStudioAtom)
      return state.plugins.find((plugin) => plugin.id === pluginId) || null
    },
})

/**
 * Selector for the active plugin's editor state
 */
export const activePluginEditorStateSelector = selector<PluginFileState | null>(
  {
    key: "pluginStudio.activePluginEditorState",
    get: ({ get }) => {
      const state = get(pluginStudioAtom)
      const activePluginId = getCodeEditorPluginId(state)

      if (!activePluginId) return null

      return state.pluginStates[activePluginId as string] || null
    },
  },
)

/**
 * Selector for open files in the active plugin
 */
export const openFilesSelector = selector<PluginFile[]>({
  key: "pluginStudio.openFiles",
  get: ({ get }) => {
    const state = get(pluginStudioAtom)
    const activePluginId = getCodeEditorPluginId(state)

    if (!activePluginId) return []

    const plugin = state.plugins.find((p) => p.id === activePluginId)
    if (!plugin) return []

    const pluginState = state.pluginStates[activePluginId as string]
    if (!pluginState) return []

    // Get all open files
    return pluginState.openFileIds
      .map((fileId) => plugin.files.find((f) => f.id === fileId))
      .filter((file): file is PluginFile => !!file)
  },
})

/**
 * Selector for a plugin's active view type (guest, host, hostconfig)
 */
export const pluginViewTypeSelector = selectorFamily<UserRole, string>({
  key: "pluginStudio.pluginViewType",
  get:
    (pluginId: string) =>
    ({ get }) => {
      const state = get(pluginStudioAtom)
      const pluginState = state.pluginStates[pluginId]
      return pluginState?.activeViewType || UserRole.GUEST
    },
  set:
    (pluginId: string) =>
    ({ get, set }, newValue) => {
      if (newValue instanceof DefaultValue) return

      const state = get(pluginStudioAtom)
      const pluginState = state.pluginStates[pluginId] || {
        activeFileId: null,
        openFileIds: [],
        activeViewType: UserRole.GUEST,
      }

      set(pluginStudioAtom, {
        ...state,
        pluginStates: {
          ...state.pluginStates,
          [pluginId]: {
            ...pluginState,
            activeViewType: newValue,
          },
        },
      })
    },
})
